import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { buildBoverketClimateDeclarationXmlFromDesign } from '@shared/helpers/boverket-climate-declaration/boverket-climate-declaration-xml.helpers';
import { getBuildingDesignFromProjectOrBuilding } from '@shared/helpers/design/design.helpers';
import {
  getExpressionVariablesRecord,
  getVariablesById,
} from '@shared/helpers/expression-variables.helpers';
import { getEmptyFrozenArray } from '@shared/helpers/array.helpers';
import {
  getBuildingLifetime,
  getProjectMeta,
} from '@shared/helpers/project.helpers';
import { getEnrichedProject, getProject } from '@client/store/project';
import { fetchProject } from '@client/store/project/project.store';
import { getSelectedVersion } from '@client/store/ui';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { getResolvedBuildingVersion } from '@shared/helpers/actions/resolved-version.helpers';
import { getProposalById } from '@shared/helpers/proposal.helpers';
import { getBuilding } from '@shared/helpers/recursive-element.helpers';
import { useRecipes } from '@client/store/recipe/recipe.hook';
import { usePvgisEnvironmentHourData } from '@client/store/pvgis';
import { useDataTables } from '@features/data-table/store/data-table-store.hooks';
import type { Baseline, ProjectID } from '@shared/models/project.model';

const FORMAT = 'yyyy-MM-dd';

const downloadXml = (xml: string, filename: string): void => {
  const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const sanitizeFilenamePart = (value: string): string =>
  value.slice(0, 50).replace(/[/\\?%*:|"<>]/g, '-');

/**
 * Prefer the version open in the UI when it belongs to this building; otherwise the first version.
 */
const resolveExportVersion = (
  building: ReturnType<typeof getBuilding>,
  selectedVersion: Baseline | undefined,
): Baseline | undefined => {
  if (
    selectedVersion &&
    building.baselines.some((version) => version.id === selectedVersion.id)
  ) {
    return selectedVersion;
  }
  return building.baselines[0];
};

/**
 * Export Boverket klimatdeklaration XML for the current project building.
 */
export const useExportBoverketXml = (): ((
  projectId?: ProjectID,
) => Promise<void>) => {
  // Include soft-deleted recipes so referenced recipes still resolve for export.
  const recipes = useRecipes(true);
  const environmentHourData = usePvgisEnvironmentHourData();
  const dataTables = useDataTables();
  const selectedProposalId = useSelectedProposalId();

  return useCallback(
    async (projectId?: ProjectID) => {
      const rawProject = projectId
        ? await fetchProject(projectId)
        : getProject();
      if (!rawProject) return;

      const project = await getEnrichedProject(rawProject, recipes);
      const building = getBuilding(project);
      const design = getBuildingDesignFromProjectOrBuilding(building);
      if (!design) {
        throw new Error(
          'Could not export Boverket XML: the building has no design.',
        );
      }

      const buildingLifetime = getBuildingLifetime(project);
      const selectedVersion = getSelectedVersion();
      const projectMeta = getProjectMeta(project);
      const enrichContext = {
        project,
        weatherHours: environmentHourData ?? getEmptyFrozenArray(),
        dataTables,
      };

      const baselineVersion = resolveExportVersion(building, selectedVersion);
      if (!baselineVersion) {
        throw new Error(
          'Could not export Boverket XML: the building has no baseline.',
        );
      }

      const proposal = selectedProposalId
        ? getProposalById(baselineVersion, selectedProposalId, false)
        : undefined;

      // Same resolved tree as {@link useResolvedVersion} (proposal actions + category enrich).
      const version = getResolvedBuildingVersion(
        baselineVersion,
        proposal ?? selectedProposalId,
        enrichContext,
      );

      const variablesRecord = getExpressionVariablesRecord(
        project,
        version,
        environmentHourData ?? getEmptyFrozenArray(),
        undefined,
        dataTables,
      );
      const versionExpressionVariables = getVariablesById(
        variablesRecord,
        version.id,
      );

      const xml = buildBoverketClimateDeclarationXmlFromDesign({
        design,
        version,
        proposal,
        buildingLifetime,
        versionExpressionVariables,
        projectMeta,
      });

      const timestamp = DateTime.now().toFormat(FORMAT);
      const projectName = sanitizeFilenamePart(project.name);
      const fileName = `${projectName} klimatdeklaration ${timestamp}.xml`;
      downloadXml(xml, fileName);
    },
    [recipes, environmentHourData, dataTables, selectedProposalId],
  );
};
